import React from 'react';
import { BarChart3, Filter, Building2, Layers } from 'lucide-react';
import { CustomerQuoteStats, MonthlyVolumePoint, QuoteFunnel, QuoteVolumeSlice } from '../../services/quoteStats';

// Angebots-Charts fuer den Cockpit-Tab. Handgebaut aus div/Flex im Stil der
// uebrigen Cockpit-Charts – das Projekt zieht bewusst keine Chart-Library.
// Rein praesentational: alle Serien kommen fertig gerechnet aus quoteStats.ts.

const eur0 = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const fmtEur = (n: number) => eur0.format(Number.isFinite(n) ? n : 0);
const fmtPct = (v: number | null) => (v === null ? '—' : `${Math.round(v)} %`);

const Panel: React.FC<{ title: string; icon: React.ReactNode; hint?: string; children: React.ReactNode }> = ({
    title,
    icon,
    hint,
    children,
}) => (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
            <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">{icon} {title}</h3>
            {hint && <span className="text-[11px] text-slate-400 text-right">{hint}</span>}
        </div>
        {children}
    </div>
);

const Empty: React.FC<{ text: string }> = ({ text }) => (
    <div className="py-12 text-center text-slate-300 text-xs font-medium">{text}</div>
);

// ─── Angebotsvolumen je Monat ───────────────────────────────────────────────

const SERIES: { key: 'won' | 'open' | 'lost'; label: string; cls: string }[] = [
    { key: 'won', label: 'Angenommen', cls: 'bg-emerald-500' },
    { key: 'open', label: 'Offen', cls: 'bg-blue-400' },
    { key: 'lost', label: 'Verloren / abgelaufen', cls: 'bg-slate-300' },
];

export const MonthlyVolumeChart: React.FC<{ points: MonthlyVolumePoint[] }> = ({ points }) => {
    const max = Math.max(1, ...points.map((p) => p.won + p.open + p.lost));

    return (
        <Panel
            title="Angebotsvolumen je Monat"
            icon={<BarChart3 className="w-4 h-4 text-blue-500" />}
            hint="netto je Angebotsmonat, max. 24 Monate"
        >
            {points.length === 0 ? (
                <Empty text="Keine Angebote im gewählten Zeitraum." />
            ) : (
                <div className="p-5">
                    <div className="flex flex-wrap gap-4 mb-4">
                        {SERIES.map((s) => (
                            <span key={s.key} className="flex items-center gap-1.5 text-[11px] font-bold text-slate-500">
                                <span className={`w-2.5 h-2.5 rounded-sm ${s.cls}`} /> {s.label}
                            </span>
                        ))}
                    </div>
                    <div className="overflow-x-auto">
                        <div className="flex items-end gap-2 min-w-max">
                            {points.map((p) => {
                                const total = p.won + p.open + p.lost;
                                return (
                                    <div key={p.month} className="flex flex-col items-center gap-1.5 w-12 shrink-0">
                                        <span className="text-[10px] font-bold text-slate-400 tabular-nums h-3">
                                            {total > 0 ? `${Math.round(total / 1000)}k` : ''}
                                        </span>
                                        <div
                                            className="w-full h-32 flex flex-col justify-end rounded-t-md overflow-hidden bg-slate-50"
                                            title={`${p.label} — angenommen ${fmtEur(p.won)}, offen ${fmtEur(p.open)}, verloren ${fmtEur(p.lost)}`}
                                        >
                                            {SERIES.map((s) => {
                                                const v = p[s.key];
                                                if (v <= 0) return null;
                                                return <div key={s.key} className={s.cls} style={{ height: `${(v / max) * 100}%` }} />;
                                            })}
                                        </div>
                                        <span className="text-[11px] font-bold text-slate-400 whitespace-nowrap">{p.label}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </Panel>
    );
};

// ─── Trichter ───────────────────────────────────────────────────────────────

export const QuoteFunnelChart: React.FC<{ funnel: QuoteFunnel }> = ({ funnel }) => {
    const base = funnel.createdCount;
    const stages = [
        { label: 'Erstellt', count: funnel.createdCount, net: funnel.createdNet, cls: 'bg-slate-400' },
        { label: 'Versendet', count: funnel.sentCount, net: funnel.sentNet, cls: 'bg-blue-500' },
        { label: 'Angenommen', count: funnel.acceptedCount, net: funnel.acceptedNet, cls: 'bg-emerald-500' },
    ];

    return (
        <Panel title="Trichter" icon={<Filter className="w-4 h-4 text-indigo-500" />} hint="dieselbe Kohorte über alle Stufen">
            {base === 0 ? (
                <Empty text="Keine Angebote im gewählten Zeitraum." />
            ) : (
                <div className="p-5 space-y-3">
                    {stages.map((s, i) => (
                        <div key={s.label}>
                            {i > 0 && (
                                <p className="text-[11px] font-bold text-slate-400 mb-1.5 pl-1">
                                    ↓ {i === 1 ? 'Versandquote' : 'Abschlussquote'}{' '}
                                    <span className="text-slate-600">{fmtPct(i === 1 ? funnel.sentRatePct : funnel.winRatePct)}</span>
                                </p>
                            )}
                            <div className="flex items-center gap-3">
                                <span className="w-24 shrink-0 text-[11px] font-bold text-slate-500">{s.label}</span>
                                <div className="flex-1 h-8 bg-slate-50 rounded-lg overflow-hidden">
                                    <div
                                        className={`h-full ${s.cls} rounded-lg transition-all`}
                                        style={{ width: `${s.count > 0 ? Math.max(4, (s.count / base) * 100) : 0}%` }}
                                    />
                                </div>
                                <span className="w-36 shrink-0 text-right text-[11px] tabular-nums">
                                    <span className="font-black text-slate-700">{s.count}</span>
                                    <span className="text-slate-400"> · {fmtEur(s.net)}</span>
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </Panel>
    );
};

// ─── Volumen je Leistungsart / Abschnitt ────────────────────────────────────

/**
 * Worin steckt das Angebotsvolumen. Anders als alle uebrigen Charts rechnet
 * diese Serie auf der POSITIONSEBENE (quoteStats.volumeByAssignment bzw.
 * volumeBySection) — die Angebotssumme allein beantwortet die Frage nicht.
 * Der gruene Anteil ist der gewonnene Teil derselben Scheibe.
 */
export const VolumeBreakdownChart: React.FC<{
    rows: QuoteVolumeSlice[];
    title: string;
    hint: string;
}> = ({ rows, title, hint }) => {
    const max = Math.max(1, ...rows.map((r) => r.net));

    return (
        <Panel title={title} icon={<Layers className="w-4 h-4 text-amber-500" />} hint={hint}>
            {rows.length === 0 ? (
                <Empty text="Keine Positionen im gewählten Zeitraum." />
            ) : (
                <div className="p-5 space-y-2.5">
                    <div className="flex flex-wrap gap-4 mb-1">
                        <span className="flex items-center gap-1.5 text-[11px] font-bold text-slate-500">
                            <span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" /> Gewonnen
                        </span>
                        <span className="flex items-center gap-1.5 text-[11px] font-bold text-slate-500">
                            <span className="w-2.5 h-2.5 rounded-sm bg-slate-300" /> Übriges Volumen
                        </span>
                    </div>
                    {rows.map((r) => (
                        <div key={r.key} className="flex items-center gap-3">
                            <span className="w-40 shrink-0 text-[11px] font-bold text-slate-500 truncate" title={r.label}>
                                {r.label}
                            </span>
                            <div
                                className="flex-1 h-7 bg-slate-50 rounded-lg overflow-hidden flex"
                                title={`${r.label} — ${fmtEur(r.net)} gesamt, davon ${fmtEur(r.wonNet)} gewonnen · ${r.itemCount} Position(en) in ${r.quoteCount} Angebot(en)`}
                            >
                                <div className="h-full bg-emerald-500" style={{ width: `${(r.wonNet / max) * 100}%` }} />
                                <div className="h-full bg-slate-300" style={{ width: `${((r.net - r.wonNet) / max) * 100}%` }} />
                            </div>
                            <span className="w-32 shrink-0 text-right text-[11px] tabular-nums">
                                <span className="font-black text-slate-700">{fmtEur(r.net)}</span>
                                <span className="text-slate-400"> · {r.itemCount}</span>
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </Panel>
    );
};

// ─── Top-Kunden ─────────────────────────────────────────────────────────────

export const TopCustomersTable: React.FC<{ rows: CustomerQuoteStats[] }> = ({ rows }) => (
    <Panel title="Top-Kunden" icon={<Building2 className="w-4 h-4 text-emerald-500" />} hint="nach gewonnenem Volumen">
        {rows.length === 0 ? (
            <Empty text="Keine Angebote im gewählten Zeitraum." />
        ) : (
            <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse min-w-[520px]">
                    <thead>
                        <tr className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase font-bold text-slate-500 tracking-wider">
                            <th className="px-5 py-3">Kunde</th>
                            <th className="px-5 py-3 text-right">Angebote</th>
                            <th className="px-5 py-3 text-right">Gewonnen</th>
                            <th className="px-5 py-3 text-right">Quote</th>
                            <th className="px-5 py-3 text-right">Volumen</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {rows.map((r) => (
                            <tr key={r.customerId} className="hover:bg-slate-50/60 transition-colors">
                                <td className="px-5 py-3 text-sm font-bold text-slate-700 truncate max-w-[220px]">{r.name}</td>
                                <td className="px-5 py-3 text-right text-sm text-slate-500 tabular-nums">{r.quoteCount}</td>
                                <td className="px-5 py-3 text-right text-sm text-slate-500 tabular-nums">{r.wonCount}</td>
                                <td className="px-5 py-3 text-right text-sm font-bold tabular-nums text-slate-600">{fmtPct(r.winRatePct)}</td>
                                <td className="px-5 py-3 text-right text-sm font-bold text-emerald-600 tabular-nums whitespace-nowrap">
                                    {fmtEur(r.wonNet)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )}
    </Panel>
);
